import { Alert, Stack } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../auth/useAuthContext';
import { BrandConfig } from '../../config/brandConfig';
import { GuestGeneral } from '../../models/GuestGeneral';
import { ReservationGeneral } from '../../models/ReservationGeneral';
import {
  useGetReservationGuestsQuery,
  usePatchRegistrationStepMutation,
} from '../../redux/rtkQuery/apiSlice';
import { PATH_APP } from '../../routes/paths';
import { HelpLink } from '../auth/VerifyCredentialForm';
import {
  RegistrationWizardContent,
  RegistrationWizardPageShell,
} from './RegistrationWizardContent';
import {
  captureDocuSignReturnEventFromUrl,
  clearDocuSignReturnEvent,
  getPersistedEnvelopeId,
  getWizardStepIndexForDocuSignReturn,
  persistEnvelopeId,
} from './docusignReturnStorage';
import { RegistrationPicklistItem, RegistrationWizardStep, WizardStepInfo } from './types';

interface RegistrationWizardProps {
  reservation: ReservationGeneral;
  registrationSteps: WizardStepInfo[];
  callbackForceGuardRefetch: (overrideBusy: boolean) => void;
  brand: BrandConfig;
}

export function RegistrationWizard({
  reservation,
  registrationSteps,
  callbackForceGuardRefetch,
  brand,
}: RegistrationWizardProps) {
  const docuSignReturnEventRef = useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<number>(() => {
    const event = captureDocuSignReturnEventFromUrl(reservation.name);
    docuSignReturnEventRef.current = event;

    const resumeIndex = getWizardStepIndexForDocuSignReturn(reservation.name, registrationSteps);
    if (resumeIndex === null) {
      return 0;
    }

    const identityIndex = registrationSteps.findIndex(
      (s) => s.key === RegistrationWizardStep.IdentityVerification
    );
    // Identity Verification owns the post-sign poll; clear so RA form does not also consume it.
    if (event === 'signing_complete' && resumeIndex === identityIndex) {
      clearDocuSignReturnEvent(reservation.name);
    }
    return resumeIndex;
  });
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const registrationStuck = useMemo(() => {
    const raStep = registrationSteps.find((s) => s.key === RegistrationWizardStep.RentalAgreement);
    if (!raStep) {
      console.warn('rental agreement step not found');
      return false;
    }
    if (!raStep.completed) {
      return false;
    }
    for (let i = 0; i < registrationSteps.length; ++i) {
      if (registrationSteps[i].key === RegistrationWizardStep.Greeting) {
        // we never complete the greeting step
        continue;
      }
      if (registrationSteps[i].order > raStep.order) {
        // this step is after RA signing; it can't block registration
        continue;
      }
    }
    return false;
  }, [registrationSteps]);

  const {
    isLoading: isLoadingGuest,
    isSuccess: isSuccessGuest,
    data: dataGuest,
    isError: isErrorGuest,
    error: errorGuest,
    refetch: refetchGuest,
  } = useGetReservationGuestsQuery({ reservationName: reservation.name });

  const [sendPatchRegistrationStep] = usePatchRegistrationStepMutation();

  // Strip DocuSign ?event= from the URL so a refresh does not re-trigger resume logic.
  useEffect(() => {
    if (!searchParams.has('event')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('event');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Patch rental agreement as soon as DocuSign reports signing_complete (wizard stays open).
  useEffect(() => {
    if (docuSignReturnEventRef.current !== 'signing_complete') {
      return;
    }
    docuSignReturnEventRef.current = null;

    const envelopeId = getPersistedEnvelopeId(reservation.name);

    const sfPayload: Record<string, unknown> = {
      reservationName: reservation.name,
      registrationStep: RegistrationPicklistItem.RENTAL_AGREEMENT,
    };
    if (envelopeId) {
      sfPayload.envelope_id = envelopeId;
    }

    sendPatchRegistrationStep(sfPayload)
      .then(() => {
        persistEnvelopeId(reservation.name, null);
        callbackForceGuardRefetch(true);
      })
      .catch((err: Error) => {
        console.error('error on patch rental agreement after DocuSign return', err?.message);
      });
  }, [callbackForceGuardRefetch, reservation.name, sendPatchRegistrationStep]);

  useEffect(() => {
    const resortDisclosuresRequired = reservation.guest_registration_requirements.includes(
      RegistrationPicklistItem.RESORT_DISCLOSURES
    );
    const completedDisclosures = reservation.guest_registration_completed?.includes(
      RegistrationPicklistItem.RESORT_DISCLOSURES
    );
    const hideResortDisclosures = Object.keys(reservation.resort_disclosures).length === 0;
    if (hideResortDisclosures && !completedDisclosures && resortDisclosuresRequired) {
      const sfPayload = {
        reservationName: reservation.name,
        registrationStep: RegistrationPicklistItem.RESORT_DISCLOSURES,
      };
      sendPatchRegistrationStep(sfPayload)
        .then((data: any) => {
          if (data.error) {
            console.error(data.error.message);
          }
        })
        .catch((putError: any) => {
          console.error('error on update stay reason', putError);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wizardError = isErrorGuest ? (errorGuest as Error) : null;

  let mainGuest: GuestGeneral | null = null;

  // the session user must be a guest which is a host
  let allowRegistration = false;

  if (isSuccessGuest && dataGuest.length > 0) {
    dataGuest.forEach((guest: GuestGeneral) => {
      if (!allowRegistration && user && user.id === guest.guest_id) {
        // session user is qualified to register this reservation
        allowRegistration = guest.isHost;
      }

      if (guest.guest_id === reservation.guest_id) {
        // guest-of-record found
        mainGuest = guest;
      }
    });
  }

  if (!user) {
    return null;
  }

  const masterLoading = isLoadingGuest;

  const handleCancel = () => {
    navigate(PATH_APP.reservations);
  };

  return (
    <>
      {registrationStuck ? (
        <RegistrationWizardPageShell
          reservationName={reservation.name}
          titleText="Guest Registration"
          onCancel={handleCancel}
        >
          <Stack spacing={2}>
            <Alert severity="error" style={{ fontSize: '1.1rem' }}>
              We are unable to complete registration in the portal. Please contact support for
              assistance.
            </Alert>
            <HelpLink labelText="Registration Support" />
          </Stack>
        </RegistrationWizardPageShell>
      ) : (
        <RegistrationWizardContent
          allowRegistration
          ageRequired
          reservation={reservation}
          mainGuest={mainGuest}
          guestModels={Array.isArray(dataGuest) ? dataGuest : null}
          sessionUser={user}
          registrationSteps={registrationSteps}
          stepIndex={step}
          setStepIndex={setStep}
          loading={masterLoading}
          error={wizardError}
          callbackRetryGuests={refetchGuest}
          callbackMainGuestChange={() => {
            // we need the reservation, since guest__c has changed, as well as the guests
            callbackForceGuardRefetch(false);
            refetchGuest();
          }}
          callbackRefreshReservation={(overrideBusy: boolean = false) => {
            // get the reservation, to let the guard close/open us as needed
            callbackForceGuardRefetch(overrideBusy);
          }}
          onCancel={handleCancel}
          brand={brand}
        />
      )}
    </>
  );
}
